import {
  RedemptionAppliedFilters,
  RedemptionSource,
} from "../models/Dashboard/Redemption/RedemptionFilters";
import {
  CommanderActionContext,
  trackCommanderAction,
} from "./commanderTracking";
import { RedemptionActionId } from "./catalog/redemptionActions";

/** GA4 custom parameter string values are truncated around 100 chars. */
const GA_PARAM_STRING_MAX = 100;

const SOURCE_LABELS: Record<RedemptionSource, string> = {
  individual: "Individual",
  onelink: "Onelink",
  all: "All",
};

const JOURNEY_STAGE_LABELS: Record<string, string> = {
  created: "Created",
  sent: "Sent",
  redeemed: "Redeemed",
  shipments: "Shipments Created",
  pending: "Pending to be redeemed",
  expired: "Expired",
};

const PRODUCT_VIEW_LABELS: Record<string, string> = {
  lines: "Redemption lines",
  pack: "By pack",
  variant: "By variant",
  city: "By city",
};

const GROUP_BY_LABELS: Record<string, string> = {
  product: "Product · variant",
  city: "By city",
};

const DRAWER_KIND_LABELS: Record<string, string> = {
  journey: "Coupon Journey",
  product: "Product drilldown",
  matrix: "Variant matrix",
};

const PRESET_LABELS: Record<string, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  fy: "This financial year",
};

/**
 * Human label for Individual / Onelink / All tabs.
 *
 * @param source - Redemption source tab id.
 * @returns Readable tab label.
 */
export function redemptionSourceLabel(source: RedemptionSource | string): string {
  return SOURCE_LABELS[source as RedemptionSource] || String(source);
}

/**
 * Virtual screen name — each source tab is a distinct screen in GA.
 *
 * @param source - Redemption source tab id.
 * @returns Screen name for GA params.
 */
export function redemptionScreenName(source: RedemptionSource | string): string {
  return `Product Redemptions › ${redemptionSourceLabel(source)}`;
}

/**
 * Virtual screen path stored on action params (not a separate page_view).
 *
 * @param source - Redemption source tab id.
 * @returns Path-style id for docs / future use (not sent on every event).
 */
export function redemptionScreenPath(source: RedemptionSource | string): string {
  return `/redemption-dashboard/${String(source || "individual")}`;
}

/**
 * Short filter brief for action_description (fits GA's ~100 char limit).
 *
 * @param filters - Applied redemption filters.
 * @returns Compact date + filter counts string.
 */
export function redemptionFilterBrief(
  filters: RedemptionAppliedFilters,
): string {
  return `${filters.start_date.format("YYYY-MM-DD")}→${filters.end_date.format("YYYY-MM-DD")} · ${filters.order_id.length} orders · ${filters.customer_id.length} customers`;
}

/**
 * @deprecated Prefer `redemptionFilterBrief` — kept so older imports do not break.
 * @param filters - Applied redemption filters.
 * @returns Longer filter summary (not sent to GA).
 */
export function redemptionFilterSummary(
  filters: RedemptionAppliedFilters,
): string {
  const parts = [
    `Tab: ${redemptionSourceLabel(filters.source)}`,
    `Dates: ${filters.start_date.format("YYYY-MM-DD")} → ${filters.end_date.format("YYYY-MM-DD")}`,
  ];
  if (filters.order_id.length > 0) {
    parts.push(
      `Orders: ${filters.order_id.slice(0, 5).join(", ")}${
        filters.order_id.length > 5 ? "…" : ""
      } (${filters.order_id.length})`,
    );
  } else {
    parts.push("Orders: none");
  }
  if (filters.customer_id.length > 0) {
    parts.push(`Customers: ${filters.customer_id.length} selected`);
  } else {
    parts.push("Customers: none");
  }
  return parts.join(" · ");
}

/**
 * Shared slim context for every Product Redemptions GA event.
 *
 * @param filters - Applied redemption filters.
 * @returns Context params merged into the GA event.
 */
export function redemptionFilterContext(
  filters: RedemptionAppliedFilters,
): CommanderActionContext {
  const source = filters.source;
  return {
    source,
    source_label: redemptionSourceLabel(source),
    screen_name: redemptionScreenName(source),
    start_date: filters.start_date.format("YYYY-MM-DD"),
    end_date: filters.end_date.format("YYYY-MM-DD"),
    order_id_count: filters.order_id.length,
    customer_id_count: filters.customer_id.length,
  };
}

/**
 * @param stage - Journey stage id.
 * @returns Readable journey stage label.
 */
export function journeyStageLabel(stage: string): string {
  return JOURNEY_STAGE_LABELS[stage] || stage;
}

/**
 * @param view - Product drilldown view id.
 * @returns Readable product view label.
 */
export function productViewLabel(view: string): string {
  return PRODUCT_VIEW_LABELS[view] || view;
}

/**
 * @param groupBy - Matrix group-by id.
 * @returns Readable group-by label.
 */
export function groupByLabel(groupBy: string): string {
  return GROUP_BY_LABELS[groupBy] || groupBy;
}

/**
 * @param kind - Drawer kind id, or null/undefined.
 * @returns Readable drawer label.
 */
export function drawerKindLabel(kind: string | null | undefined): string {
  if (!kind) return "Drawer";
  return DRAWER_KIND_LABELS[kind] || kind;
}

/**
 * Caps a description to GA's practical string limit.
 *
 * @param text - Full description.
 * @returns Truncated description.
 */
function clipDescription(text: string): string {
  return text.slice(0, GA_PARAM_STRING_MAX);
}

/**
 * Readable card title for the open drawer (journey stage / product / matrix).
 *
 * @param extra - Label inputs from the track call.
 * @returns Human card title.
 */
function redemptionCardTitle(extra?: {
  cardTitle?: string;
  journeyStage?: string;
  productName?: string;
  productId?: number;
  drawerKind?: string | null;
}): string {
  if (extra?.cardTitle?.trim()) return extra.cardTitle.trim();
  if (extra?.drawerKind === "journey" && extra?.journeyStage) {
    return journeyStageLabel(extra.journeyStage);
  }
  if (extra?.drawerKind === "product") {
    return extra?.productName || `Product #${extra?.productId ?? "?"}`;
  }
  if (extra?.drawerKind === "matrix") {
    return "Variant matrix";
  }
  return drawerKindLabel(extra?.drawerKind);
}

/**
 * Builds `redemp_db_{drawer}_{verb}` (journey includes stage) so DebugView
 * shows which card the user is acting in. Names stay ≤40 chars.
 *
 * @param drawerKind - journey | product | matrix.
 * @param verb - open | close | sort | page | rows | export | formula | …
 * @param journeyStage - Journey stage id when kind is journey.
 * @returns Event name ≤40 chars.
 */
function redemptionDrawerEventName(
  drawerKind: string | null | undefined,
  verb: string,
  journeyStage?: string | null,
): string {
  const kind = String(drawerKind || "drawer").replace(/[^a-z0-9_]/gi, "_");
  if (kind === "journey" && journeyStage) {
    const stage = String(journeyStage)
      .replace(/[^a-z0-9_]/gi, "_")
      .slice(0, 12);
    return `redemp_db_j_${stage}_${verb}`.slice(0, 40);
  }
  return `redemp_db_${kind}_${verb}`.slice(0, 40);
}

type LabelExtra = {
  previousSource?: string;
  journeyStage?: string;
  journeyCount?: number | string;
  productId?: number;
  productName?: string;
  productView?: string;
  previousProductView?: string;
  groupBy?: string;
  previousGroupBy?: string;
  drawerKind?: string | null;
  cardTitle?: string;
  sortColumn?: string;
  sortDir?: string;
  page?: number;
  pageSize?: number;
  previousPageSize?: number;
  preset?: string;
  expanded?: boolean;
};

/**
 * Build dynamic action_name + action_description for GA (overrides catalog defaults).
 *
 * @param actionId - Stable redemption action id.
 * @param filters - Filters active for this action.
 * @param extra - Optional action-specific label inputs.
 * @returns Override labels for trackCommanderAction.
 */
export function redemptionActionLabels(
  actionId: string,
  filters: RedemptionAppliedFilters,
  extra?: LabelExtra,
): Pick<CommanderActionContext, "action_name" | "action_description"> {
  const tab = redemptionSourceLabel(filters.source);
  const brief = redemptionFilterBrief(filters);
  const card = redemptionCardTitle(extra);
  const start = filters.start_date?.format("YYYY-MM-DD") || "";
  const end = filters.end_date?.format("YYYY-MM-DD") || "";
  const dateBrief = start && end ? `${start}→${end}` : brief;

  switch (actionId) {
    case "dashboard_view":
      return {
        action_name: `Opened ${tab} redemptions`,
        action_description: clipDescription(
          `Loaded Product Redemptions ${tab} summary. ${brief}`,
        ),
      };
    case "source_change": {
      const from = redemptionSourceLabel(extra?.previousSource || "");
      return {
        action_name: `Switched to ${tab} tab`,
        action_description: clipDescription(
          `Left ${from}, opened ${tab} redemptions. ${brief}`,
        ),
      };
    }
    case "filter_apply":
      return {
        action_name: `Applied filters on ${tab}`,
        action_description: clipDescription(
          `Applied filters and refreshed ${tab}. ${brief}`,
        ),
      };
    case "filter_clear":
      return {
        action_name: `Cleared filters on ${tab}`,
        action_description: clipDescription(
          `Cleared date/order/customer filters on ${tab}. ${brief}`,
        ),
      };
    case "dashboard_refresh":
      return {
        action_name: `Refreshed ${tab} redemptions`,
        action_description: clipDescription(
          `Reloaded ${tab} summary with applied filters. ${brief}`,
        ),
      };
    case "drawer_refresh":
      return {
        action_name: `Refreshed card: ${card}`,
        action_description: clipDescription(
          `Reloaded "${card}" (${drawerKindLabel(extra?.drawerKind)}) on ${tab}. ${brief}`,
        ),
      };
    case "date_preset_select": {
      const presetId = extra?.preset || "date";
      const presetLabel =
        PRESET_LABELS[presetId] || String(presetId).toUpperCase();
      return {
        action_name: `Selected ${presetLabel}`,
        action_description: clipDescription(
          `Picked date preset ${presetLabel} on ${tab} (not applied yet)`,
        ),
      };
    }
    case "date_range_change":
      return {
        action_name: `Picked custom dates ${dateBrief}`,
        action_description: clipDescription(
          `Changed custom date range to ${dateBrief} on ${tab} (draft)`,
        ),
      };
    case "journey_card_click": {
      const stage = journeyStageLabel(extra?.journeyStage || "");
      const countPart =
        extra?.journeyCount != null ? ` · count ${extra.journeyCount}` : "";
      return {
        action_name: `Opened card: ${stage}`,
        action_description: clipDescription(
          `Opened Coupon Journey "${stage}" on ${tab}${countPart}. ${brief}`,
        ),
      };
    }
    case "product_row_click":
      return {
        action_name: `Opened card: ${card}`,
        action_description: clipDescription(
          `Opened product drilldown for "${card}" (id ${extra?.productId}) on ${tab}`,
        ),
      };
    case "variant_matrix_open":
      return {
        action_name: "Opened card: Variant matrix",
        action_description: clipDescription(
          `Opened Product · variant · count matrix on ${tab}. ${brief}`,
        ),
      };
    case "drawer_close":
      return {
        action_name: `Closed card: ${card}`,
        action_description: clipDescription(
          `Closed "${card}" (${drawerKindLabel(extra?.drawerKind)}) on ${tab}`,
        ),
      };
    case "product_view_change": {
      const view = productViewLabel(extra?.productView || "");
      const prev = productViewLabel(extra?.previousProductView || "");
      return {
        action_name: `${card} view → ${view}`,
        action_description: clipDescription(
          `On "${card}": switched ${prev} → ${view} on ${tab}`,
        ),
      };
    }
    case "matrix_groupby_change": {
      const group = groupByLabel(extra?.groupBy || "");
      const prev = groupByLabel(extra?.previousGroupBy || "");
      return {
        action_name: `Matrix group → ${group}`,
        action_description: clipDescription(
          `On Variant matrix: switched ${prev} → ${group} on ${tab}`,
        ),
      };
    }
    case "table_sort":
      return {
        action_name: `Sorted ${card} by ${extra?.sortColumn} ${extra?.sortDir}`,
        action_description: clipDescription(
          `On "${card}": sorted by ${extra?.sortColumn} ${extra?.sortDir} on ${tab}`,
        ),
      };
    case "table_page_change":
      return {
        action_name: `${card} page → ${extra?.page}`,
        action_description: clipDescription(
          `On "${card}": went to page ${extra?.page} (size ${extra?.pageSize}) on ${tab}`,
        ),
      };
    case "table_page_size_change":
      return {
        action_name: `${card} rows → ${extra?.pageSize}`,
        action_description: clipDescription(
          `On "${card}": rows ${extra?.previousPageSize} → ${extra?.pageSize} on ${tab}`,
        ),
      };
    case "export_csv":
      return {
        action_name: `Exported CSV: ${card}`,
        action_description: clipDescription(
          `Exported CSV for "${card}" on ${tab}. ${brief}`,
        ),
      };
    case "search_panel_toggle":
      return {
        action_name: `${extra?.expanded ? "Expanded" : "Collapsed"} search on ${tab}`,
        action_description: clipDescription(
          `${extra?.expanded ? "Expanded" : "Collapsed"} Search filters panel on ${tab}`,
        ),
      };
    case "formula_toggle":
      return {
        action_name: `${extra?.expanded ? "Expanded" : "Collapsed"} formula · ${card}`,
        action_description: clipDescription(
          `${extra?.expanded ? "Expanded" : "Collapsed"} How this is calculated on "${card}"`,
        ),
      };
    default:
      return {
        action_name: undefined,
        action_description: clipDescription(
          `Action "${actionId}" on ${tab}. ${brief}`,
        ),
      };
  }
}

type RedemptionTrackExtra = LabelExtra & {
  /** Extra raw context fields merged into the event. */
  context?: CommanderActionContext;
};

/**
 * Preferred helper for Product Redemptions GA: readable labels + slim filter/screen context.
 * Fires exactly one GA event per call (no duplicate rollup / page_view).
 *
 * @param actionId - Catalog action id.
 * @param filters - Applied filters for this action.
 * @param extra - Optional label inputs + raw context fields.
 */
export function trackRedemptionAction(
  actionId: RedemptionActionId,
  filters: RedemptionAppliedFilters,
  extra?: RedemptionTrackExtra,
): void {
  try {
    const { context: extraContext, ...labelExtra } = extra || {};

    // Pattern: {module}_db_{action} — `db` = dashboard. Never `pr_` (Purchase Request).
    const source = filters.source;
    const drawerKind = labelExtra.drawerKind;
    const journeyStage = labelExtra.journeyStage;
    const cardTitle = redemptionCardTitle(labelExtra);

    let eventName = `redemp_db_${actionId}`;
    if (actionId === "dashboard_view" || actionId === "source_change") {
      eventName = `redemp_db_tab_${source}`.slice(0, 40);
    } else if (actionId === "filter_apply") {
      eventName = `redemp_db_filters_apply_${source}`.slice(0, 40);
    } else if (actionId === "filter_clear") {
      eventName = `redemp_db_filters_clear_${source}`.slice(0, 40);
    } else if (actionId === "date_preset_select") {
      const presetKey = String(labelExtra.preset || "date")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      eventName = `redemp_db_preset_${presetKey || "date"}`.slice(0, 40);
    } else if (actionId === "dashboard_refresh") {
      eventName = `redemp_db_refresh_${source}`.slice(0, 40);
    } else if (actionId === "drawer_refresh") {
      eventName = redemptionDrawerEventName(drawerKind, "refresh", journeyStage);
    } else if (actionId === "date_range_change") {
      eventName = "redemp_db_custom_dates";
    } else if (actionId === "search_panel_toggle") {
      eventName = "redemp_db_filters_panel";
    } else if (actionId === "journey_card_click" && journeyStage) {
      eventName = `redemp_db_journey_${journeyStage}`.slice(0, 40);
    } else if (actionId === "product_row_click") {
      eventName = redemptionDrawerEventName("product", "open");
    } else if (actionId === "variant_matrix_open") {
      eventName = redemptionDrawerEventName("matrix", "open");
    } else if (actionId === "drawer_close") {
      eventName = redemptionDrawerEventName(drawerKind, "close", journeyStage);
    } else if (actionId === "table_sort") {
      eventName = redemptionDrawerEventName(drawerKind, "sort", journeyStage);
    } else if (actionId === "table_page_change") {
      eventName = redemptionDrawerEventName(drawerKind, "page", journeyStage);
    } else if (actionId === "table_page_size_change") {
      eventName = redemptionDrawerEventName(drawerKind, "rows", journeyStage);
    } else if (actionId === "export_csv") {
      eventName = redemptionDrawerEventName(drawerKind, "export", journeyStage);
    } else if (actionId === "formula_toggle") {
      eventName = redemptionDrawerEventName(
        drawerKind,
        "formula",
        journeyStage,
      );
    } else if (actionId === "product_view_change") {
      const view = String(labelExtra.productView || "view")
        .replace(/[^a-z0-9_]/gi, "_")
        .slice(0, 12);
      eventName = `redemp_db_product_view_${view}`.slice(0, 40);
    } else if (actionId === "matrix_groupby_change") {
      const group = String(labelExtra.groupBy || "group")
        .replace(/[^a-z0-9_]/gi, "_")
        .slice(0, 12);
      eventName = `redemp_db_matrix_by_${group}`.slice(0, 40);
    }

    trackCommanderAction({
      moduleId: "product_redemptions",
      actionId,
      eventName,
      context: {
        ...redemptionFilterContext(filters),
        ...redemptionActionLabels(actionId, filters, labelExtra),
        ...(drawerKind
          ? {
              drawer_kind: drawerKind,
              card_title: cardTitle,
              ...(journeyStage ? { journey_stage: journeyStage } : {}),
            }
          : {}),
        ...(extraContext || {}),
      },
    });
  } catch {
    // never block UI
  }
}
